// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { Resources } from '@shared/localizations/Resources';
import { AssetBalanceItem } from '../cache/AssetBalanceItem';
import { QuickTableRactiveEvents } from '../elements/QuickTable/QuickTableRactive';
import { PanelNames } from '../UtilsClasses/FactoryConstants';
import { AccountType } from '@shared/utils/cache/Accounts/Account.types';
import { DataCache } from '@shared/commons/DataCache';
import { SessionSettings } from '@shared/commons/SessionSettings';
import { ApplicationPanelWithTable } from './ApplicationPanelWithTable';
import { AssetBalanceItemColumnsEnum } from '@shared/utils/Enums/AccountDetailsEnum';
export const PANEL_ASSET_BALANCES_KEY = 'panel.assetBalances';
export class AssetBalancesPanel extends ApplicationPanelWithTable {
    constructor() {
        super(...arguments);
        this.headerLocaleKey = PANEL_ASSET_BALANCES_KEY; // For reference, the identical key 'panel.accounts' has the same set of translations and is stored in PanelLocKeys[PanelNames.AssetBalancesPanel].
    }
    getType() {
        return PanelNames.AssetBalancesPanel;
    }
    oncomplete() {
        super.oncomplete();
        const qt = this.getQuickTable();
        qt.OnSelectionChanged.Subscribe(this.onSelectionChanged, this);
        qt.skipAccountFiltration = true; // #120237
        this.quickTableRactive.on(QuickTableRactiveEvents.DoubleClick, this.onDoubleClick.bind(this));
    }
    onSelectionChanged() {
        const accountID = this.getSelectedAccountId();
        if (isValidString(accountID)) {
            this.accountLink_Out(false, DataCache.GetAccountById(accountID));
        }
    }
    getSelectedAccountId() {
        const qt = this.getQuickTable();
        const selectedRowsId = qt.selectedRowIds;
        if (!isValidArray(selectedRowsId))
            return;
        let accId = '';
        if (selectedRowsId.length > 0) {
            const row = qt.rows[selectedRowsId[0]];
            const item = !isNullOrUndefined(row) ? row.item : null;
            if (isNullOrUndefined(item)) {
                const acc = item.GetCurrentAccount();
                if (!isNullOrUndefined(acc)) {
                    accId = acc.BstrAccount;
                }
            }
        }
        return accId;
    }
    populateItemsDirect() {
        super.populateItemsDirect();
        const accs = DataCache.Accounts;
        const accsKeys = Object.keys(accs);
        for (let i = 0; i < accsKeys.length; i++) {
            const acc = accs[accsKeys[i]];
            for (const key in acc.assetBalances) {
                const asd = acc.assetBalances[key];
                if (acc.AccountType !== AccountType.MultiAsset || !acc.assetBalanceDefault.IsEmpty) {
                    this.getQuickTable().AddItem(new AssetBalanceItem(acc, SessionSettings, asd, asd.Asset));
                }
            }
        }
        DataCache.OnAddAccount.Subscribe(this.DataCacheOnAddAccountEvent, this);
        DataCache.OnRemoveAccount.Subscribe(this.DataCacheOnRemoveAccountEvent, this);
    }
    SetColumnsDefaultDisplayIndex(table) {
        const columns = table.columns;
        const displayIndexes = [
            AssetBalanceItemColumnsEnum.ACCOUNT_COL_INDEX,
            AssetBalanceItemColumnsEnum.ASSET_NAME_COL_INDEX,
            AssetBalanceItemColumnsEnum.BALANCE_COL_INDEX,
            AssetBalanceItemColumnsEnum.AVAILABLE_FUNDS_COL_INDEX,
            AssetBalanceItemColumnsEnum.INITIAL_MARGIN_REQ_COL_INDEX,
            AssetBalanceItemColumnsEnum.TODAY_VOLUME_COL_INDEX,
            AssetBalanceItemColumnsEnum.TODAY_TRADES_COUNT_COL_INDEX,
            AssetBalanceItemColumnsEnum.ACCOUNT_TYPE_COL_INDEX,
            AssetBalanceItemColumnsEnum.WITHDRAWAL_AVAILABLE_COL_INDEX,
            AssetBalanceItemColumnsEnum.INTEREST_RATE_COL_INDEX,
            AssetBalanceItemColumnsEnum.NEGATIVE_INTEREST_RATE_COL_INDEX
        ];
        displayIndexes.forEach((index, i) => {
            columns[index].displayedIndex = i;
        });
    }
    updatePanelHeader() {
        void this.set({
            header: Resources.getResource(this.headerLocaleKey) +
                (this.NeedCalculateRowCount ? '  (' + this.rowCount + ')' : '')
        });
    }
    filterByAccountNumber() { }
    dispose() {
        DataCache.OnAddAccount.UnSubscribe(this.DataCacheOnAddAccountEvent, this);
        DataCache.OnRemoveAccount.UnSubscribe(this.DataCacheOnRemoveAccountEvent, this);
        super.dispose();
    }
    DataCacheOnAddAccountEvent(acc) {
        if (isNullOrUndefined(acc) ||
            isNullOrUndefined(this.quickTableRactive) ||
            isNullOrUndefined(this.getQuickTable())) {
            return;
        }
        const quickTable = this.getQuickTable();
        for (const key in acc.assetBalances) {
            const row = quickTable.getItemById(acc.AcctNumber + ':' + key);
            if (row != null) {
                row.updateItem(row.item);
            }
            else {
                const aBal = acc.assetBalances[key];
                if (!aBal.IsEmpty) {
                    quickTable.AddItem(new AssetBalanceItem(acc, SessionSettings, aBal, aBal.Asset));
                }
            }
        }
    }
    DataCacheOnRemoveAccountEvent(acc) {
        this.getQuickTable().RemoveItem(acc.userID);
    }
}
ApplicationPanelWithTable.extendWith(AssetBalancesPanel, {
    data: function () {
        return { isAccountLinkShow: true, canFilterByAccount: false };
    },
    headerLocaleKey: PANEL_ASSET_BALANCES_KEY
});
